import { Trans } from "@lingui/react/macro";
import { i18n } from "@lingui/core";
import dayjs from "dayjs";
import { RiBox1Line, RiServiceLine } from "react-icons/ri";
import { CalendarDays, Clock, Heart, Users } from "lucide-react";
import { FiGlobe } from "react-icons/fi";
import { Avatar, Chip, Divider } from "@heroui/react";
import { LinkifiedText } from "@/Components/NextBase/LinkifiedText";
import { MarketplaceBookingIndicator } from "@/Components/NextBase/Appointment/MarketplaceBookingIndicator";
import type { FeatureId } from "@/server-types";
import { useClientFeature } from "@/hooks/useClientFeature";
import { useStaffRequestIndicatorVisible } from "@/hooks/useStaffRequestIndicator";

import localizedFormat from "dayjs/plugin/localizedFormat";
dayjs.extend(localizedFormat);

type Customer = {
  name: string;
  lastName: string;
  email?: string;
  gsm?: string;
  comment?: string;
  appointmentNoteTags?: string[];
  bookedFromForm: boolean;
  bookedFromMarketplace?: boolean;
  staffRequested?: boolean;
  attendance?: string;
  extraDetails?: Array<{
    id: string;
    type: "text" | "textarea" | "radio" | "select" | string;
    label: {
      [key: string]: string;
    };
    value:
      | string
      | {
          [key: string]: string;
        };
  }>;
  contactCustomer?: {
    customerId: number;
    name: string;
    lastName: string;
    email?: string | null;
    gsm?: string | null;
  } | null;
};

type Resource = {
  label: string;
  id: number;
};

type FormattedData = {
  startTime: string;
  endTime: string;
  date: string;
};

type Appointment = {
  formattedData: FormattedData;
  serviceName: string;
  customers: Customer[];
  resources: number[];
  userIds: number[];
  numberOfPeople: number | undefined;
};

type HoverContentProps = {
  appointment: Appointment;
  viewCustomersPermission: boolean;
  resources: Resource[];
  users: User[];
};

type User = {
  userId: number;
  name: string;
  lastName: string;
  fullImageUrl?: string;
};

const CONTACT_CUSTOMER_FEATURE_ID: FeatureId = "contactCustomer";

export const HoverContent = ({
  appointment,
  viewCustomersPermission,
  resources,
  users,
}: HoverContentProps) => {
  const { isFeatureEnabled } = useClientFeature();
  const isContactCustomerFeatureEnabled = isFeatureEnabled(
    CONTACT_CUSTOMER_FEATURE_ID,
  );
  const isStaffRequestIndicatorVisible = useStaffRequestIndicatorVisible();
  const { formattedData, serviceName, customers = [] } = appointment;

  const { startTime, endTime, date } = formattedData;

  const { locale } = i18n;
  const appointmentDate = dayjs(date, "DD.MM.YYYY").locale(locale).format("LL");
  const appointmentTime = `${startTime} - ${endTime}`;

  const groupAppointment = appointment.numberOfPeople
    ? appointment.numberOfPeople > 1
    : false;

  return (
    <div className="flex max-w-72 flex-col gap-2 px-2 py-3">
      <div className="flex items-center gap-2">
        <CalendarDays size={16} />
        <p className="text-sm">{appointmentDate}</p>
      </div>
      <div className="flex items-center gap-2">
        <Clock size={16} />
        <p className="text-sm">{appointmentTime}</p>
      </div>
      <Divider />
      <div className="flex items-center gap-2">
        <RiServiceLine className="shrink-0" />
        <p className="text-sm">{serviceName}</p>
      </div>
      {customers.length > 0 &&
        customers[0].attendance &&
        Number(customers[0].attendance) > 1 && (
          <div className="flex items-center gap-2">
            <Users size={16} />
            <p className="text-sm text-gray-500">
              {customers[0].attendance} <Trans>oseb</Trans>
            </p>
          </div>
        )}
      {appointment.resources.length > 0 && (
        <div className="flex items-center gap-2">
          <RiBox1Line />
          <p className="text-sm text-gray-500">
            {appointment.resources
              .map(
                (resourceId) =>
                  resources.find((resource) => resource.id === resourceId)
                    ?.label,
              )
              .filter(Boolean)
              .join(", ")}
          </p>
        </div>
      )}
      {appointment.userIds.map((userId) => {
        const user = users.find((u) => u.userId === userId);
        if (!user) return null;
        return (
          <div key={userId} className="flex items-center gap-2">
            <Avatar
              size="sm"
              radius="md"
              src={user.fullImageUrl}
              name={`${user.name[0]}${user.lastName?.[0]}`}
            />
            <p className="text-sm">
              {user.name} {user.lastName}
            </p>
          </div>
        );
      })}

      {viewCustomersPermission && (
        <>
          <Divider />
          <div className="mt-2 flex flex-col gap-2">
            {customers.length > 0 ? (
              <>
                {groupAppointment && (
                  <p className="text-sm text-gray-500">
                    <Trans>
                      Št. strank: {customers.length} /{" "}
                      {appointment.numberOfPeople}
                    </Trans>
                  </p>
                )}
                {customers.map((customer, index) => (
                  <div key={`${customer.name}-${customer.lastName}-${index}`}>
                    <div className="flex items-center">
                      <Avatar
                        size="sm"
                        radius="md"
                        style={{
                          display: "inline-block",
                          marginRight: "8px",
                          textAlign: "center",
                        }}
                        name={`${customer.name?.[0] ?? ""}${customer.lastName?.[0] ?? ""}`}
                      />
                      <p className="text-sm whitespace-nowrap">
                        {customer.name ?? ""} {customer.lastName ?? ""}{" "}
                        {customer.bookedFromMarketplace ? (
                          <MarketplaceBookingIndicator
                            size={14}
                            className="align-text-bottom"
                          />
                        ) : customer.bookedFromForm ? (
                          <FiGlobe
                            style={{
                              display: "inline-block",
                            }}
                          />
                        ) : null}
                        {isStaffRequestIndicatorVisible &&
                        customer.staffRequested ? (
                          <Heart
                            size={14}
                            fill="currentColor"
                            className="ml-1 inline-block align-text-bottom"
                          />
                        ) : null}
                      </p>
                    </div>
                    <div className="flex flex-col items-start">
                      {customer.appointmentNoteTags &&
                        customer.appointmentNoteTags.length > 0 && (
                          <div className="mt-1 flex flex-wrap gap-1">
                            {customer.appointmentNoteTags.map((tagName) => (
                              <Chip
                                key={tagName}
                                variant="flat"
                                size="sm"
                                color="primary"
                              >
                                {tagName}
                              </Chip>
                            ))}
                          </div>
                        )}
                      {customer.email && !groupAppointment && (
                        <p className="text-xs text-gray-500">
                          {customer.email}
                        </p>
                      )}
                      {customer.gsm && (
                        <p className="text-xs text-gray-500">{customer.gsm}</p>
                      )}
                      {isContactCustomerFeatureEnabled &&
                        customer.contactCustomer && (
                          <div className="mt-2 w-full rounded-xl border border-slate-200 bg-slate-50/70 px-3 py-2 shadow-sm">
                            <p className="text-[11px] font-semibold tracking-wide text-slate-600 uppercase">
                              <Trans>Kontaktna oseba</Trans>
                            </p>
                            <p className="mt-0.5 text-sm font-medium text-slate-800">
                              {customer.contactCustomer.name}{" "}
                              {customer.contactCustomer.lastName}
                            </p>
                            {customer.contactCustomer.email && (
                              <p className="text-xs text-slate-500">
                                {customer.contactCustomer.email}
                              </p>
                            )}
                            {customer.contactCustomer.gsm && (
                              <p className="text-xs text-slate-500">
                                {customer.contactCustomer.gsm}
                              </p>
                            )}
                          </div>
                        )}
                      {customer.comment && (
                        <div className="flex w-full items-center gap-2 rounded-md bg-[#f5f5f5] p-2 whitespace-normal">
                          <p className="text-xs wrap-break-word whitespace-normal text-gray-500">
                            <LinkifiedText
                              text={customer.comment}
                              linkClassName="text-xs"
                            />
                          </p>
                        </div>
                      )}

                      {Array.isArray(customer.extraDetails) &&
                        customer.extraDetails.length > 0 && (
                          <div className="mt-1 w-full rounded-lg bg-[#F7F7F7] px-2 py-1">
                            <p className="text-sm font-medium">
                              <Trans>Polja po meri</Trans>
                            </p>
                            <div className="flex flex-col gap-1">
                              {customer.extraDetails.map((field) => {
                                let displayValue = "";

                                if (field.type === "date") {
                                  displayValue = dayjs(field.value as string, [
                                    "MM-DD-YYYYTHH:mm",
                                    "YYYY-MM-DD",
                                  ])
                                    .toDate()
                                    .toLocaleString(locale, {
                                      day: "2-digit",
                                      month: "numeric",
                                      year: "numeric",
                                    });
                                } else if (typeof field.value === "string") {
                                  displayValue = field.value;
                                } else if (
                                  field.type === "radio" ||
                                  field.type === "select"
                                ) {
                                  displayValue =
                                    field.value.en ||
                                    Object.values(field.value)[0];
                                }

                                const label =
                                  field.label.en ||
                                  Object.values(field.label)[0];

                                return (
                                  <div className="flex gap-1" key={field.id}>
                                    <p className="text-sm font-medium">
                                      {label}:{" "}
                                    </p>
                                    <p className="text-sm">{displayValue}</p>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                    </div>
                  </div>
                ))}
              </>
            ) : (
              <p className="text-sm text-gray-500">
                <Trans>Na terminu še ni nobene stranke</Trans>
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
};
